import styled from 'styled-components'
import { getDashColor } from '../../styles'

// Paleta alinhada às telas existentes (tema claro nos dois modos de marca):
// texto escuro sobre superfícies claras.
const TEXT = () => getDashColor('PRIMARY_BLUE_02', 'GRAY_DARK')
const TEXT_MUTED = () => getDashColor('GRAY_SCALE_05', 'GRAY_DARK')
const SURFACE = () => getDashColor('WHITE', 'BEIGE')
const SURFACE_SUBTLE = () => getDashColor('GRAY_SCALE_09', 'BEIGE')
const BORDER = () => getDashColor('GRAY_SCALE_08', 'GRAY_SCALE_07')
const ACCENT = () => getDashColor('SECONDARY_BLUE_01', 'PRIMARY_BLUE_02')
const POSITIVE = () => getDashColor('SECONDARY_GREEN_01', 'SECONDARY_GREEN_04')

export const Container = styled.div`
  padding: 16px 24px 48px;
  max-width: 1100px;
  margin: 0 auto;
`

export const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${TEXT};
  margin: 8px 0 2px;
`

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${TEXT_MUTED};
  margin: 0 0 16px;
`

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${BORDER};
  margin: 16px 0;
`

export const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
`

export const SummaryCard = styled.div`
  flex: 1 1 180px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  padding: 16px;
  background: ${SURFACE};
`

export const CardLabel = styled.span`
  display: block;
  font-size: 13px;
  color: ${TEXT_MUTED};
  margin-bottom: 6px;
`

export const InfoHint = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  margin-left: 6px;
  border-radius: 50%;
  border: 1px solid ${BORDER};
  color: ${TEXT_MUTED};
  font-size: 10px;
  font-weight: 700;
  cursor: help;
  vertical-align: text-top;
`

export const CardValue = styled.strong`
  font-size: 22px;
  font-weight: 700;
  color: ${TEXT};
`

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`

export const ToggleGroup = styled.div`
  display: inline-flex;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  overflow: hidden;
`

export const ToggleButton = styled.button<{ active: boolean }>`
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  background: ${({ active }) => (active ? ACCENT() : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : TEXT_MUTED())};
`

export const CheckboxLabel = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ disabled }) => (disabled ? TEXT_MUTED() : TEXT())};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  input {
    accent-color: ${ACCENT};
    cursor: inherit;
  }
`

export const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${BORDER};
  font-size: 14px;
  background: ${SURFACE};
  color: ${TEXT};
`

export const CalendarWrap = styled.div`
  .react-calendar {
    width: 100%;
    border: 1px solid ${BORDER};
    border-radius: 12px;
    font-family: inherit;
    background: ${SURFACE};
    padding: 8px;
    color: ${TEXT};
  }
  .react-calendar button {
    color: ${TEXT};
  }
  .react-calendar__month-view__days__day--weekend {
    color: ${() => getDashColor('PRIMARY_RED_02', 'PRIMARY_RED_02')};
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${TEXT_MUTED};
  }
  .react-calendar__navigation button {
    color: ${TEXT};
    font-weight: 600;
  }
  .react-calendar__tile {
    height: 92px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    padding-top: 6px;
    border-radius: 8px;
  }
  .react-calendar__tile--now {
    background: ${SURFACE_SUBTLE};
  }
  .react-calendar__tile:enabled:hover {
    background: ${() => getDashColor('SECONDARY_BLUE_10', 'GRAY_SCALE_09')};
  }
  .react-calendar__tile--active,
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: ${() => getDashColor('SECONDARY_BLUE_10', 'GRAY_SCALE_09')};
    color: ${TEXT};
    box-shadow: inset 0 0 0 2px ${ACCENT};
  }
  /* mantém texto do dia/valor/quantidade legível no dia selecionado */
  .react-calendar__tile--active abbr {
    color: ${TEXT};
  }
  .react-calendar__month-view__weekdays {
    font-size: 12px;
    text-transform: capitalize;
    color: ${TEXT_MUTED};
  }
  .react-calendar__month-view__weekdays abbr {
    text-decoration: none;
  }
`

export const TileValue = styled.span`
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  color: ${POSITIVE};
`

export const TileQtd = styled.span`
  font-size: 10px;
  color: ${TEXT_MUTED};
`

export const TileAdvance = styled.span`
  font-size: 10px;
  color: ${TEXT_MUTED};
  text-decoration: line-through;
`

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  color: ${TEXT};
  th,
  td {
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid ${BORDER};
    color: ${TEXT};
  }
  th {
    color: ${TEXT_MUTED};
    font-weight: 600;
  }
  td.value {
    font-weight: 700;
    color: ${TEXT};
  }
  tbody tr {
    cursor: pointer;
  }
  tbody tr:hover td {
    background: ${SURFACE_SUBTLE};
  }
`

export const DetailPanel = styled.div`
  margin-top: 24px;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  padding: 16px;
  background: ${SURFACE};
  overflow-x: auto;
`

export const DetailTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 12px;
  color: ${TEXT};
`

export const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: ${TEXT_MUTED};
  font-size: 14px;
`

export const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  background: ${SURFACE_SUBTLE};
  color: ${TEXT_MUTED};
`
